// Path: src\Components\Rewards\BuyPacks\BuyCoinPopup.jsx
import { React, useEffect, useState, useCallback, useRef } from "react";
import TextFieldWithButton from "../../CommonFields/Textfieldwithbutton";
import { url } from "../../../config/url";
import { easeBuzzHash, easeBuzzPaymentStatus, userByToken } from "../../../api/auth.api";
import { checkEmail, validateEmailOtp } from "../../../api/account.api";
import { toastError, toastSuccess } from "../../../Utilities/toast";
import Countdown from "react-countdown";
import { toast } from "react-toastify";

// Constants
const COUNTDOWN_DURATION = 90; // seconds
const OTP_LENGTH = 6;
const INDIA_COUNTRY_ID = '611e04284ac17121fd8b1a54';
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// Custom hooks
const useEaseBuzzCheckout = () => {
  const easebuzzCheckoutRef = useRef(null);
  
  useEffect(() => {
    if (window.EasebuzzCheckout) {
      easebuzzCheckoutRef.current = new window.EasebuzzCheckout(
        process.env.REACT_APP_EASEBUZZ_API_KEY,
        process.env.REACT_APP_EASEBUZZ_API_MODE
      );
    }
    
    return () => {
      easebuzzCheckoutRef.current = null;
    };
  }, []);
  
  return easebuzzCheckoutRef.current;
};

const BuyCoinPopup = ({
  isOpen,
  title,
  onClose,
  reedemItem,
  handleReedeemed,
  isReward,
  isBundle,
  children,
}) => {
  // State management
  const [formState, setFormState] = useState({
    email: "",
    otp: "",
    isOtpSent: false,
    emailError: "",
    otpError: "",
    isLoading: false,
    isProcessing: false,
  });
  
  const [userDetails, setUserDetails] = useState(null);
  const [countdownKey, setCountdownKey] = useState(0);
  
  const easebuzzCheckout = useEaseBuzzCheckout();

  // Utility functions
  const validateEmail = useCallback((email) => {
    return EMAIL_REGEX.test(email.toLowerCase());
  }, []);

  const validateOtp = useCallback((otp) => {
    return /^\d{6}$/.test(otp);
  }, []);

  const resetFormErrors = useCallback(() => {
    setFormState(prev => ({
      ...prev,
      emailError: "",
      otpError: ""
    }));
  }, []);

  const setFormError = useCallback((field, error) => {
    setFormState(prev => ({
      ...prev,
      [field]: error
    }));
  }, []);

  const updateFormField = useCallback((field, value) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // API functions
  const fetchUserDetails = useCallback(async () => {
    updateFormField('isLoading', true);
    try {
      const response = await userByToken();
      if (response?.success && response?.data) {
        setUserDetails(response.data);
        updateFormField('email', response.data.email || '');
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      toastError('Failed to fetch user details');
    } finally {
      updateFormField('isLoading', false);
    }
  }, [updateFormField]);

  const handleSendOtp = useCallback(async () => {
    resetFormErrors();
    
    if (!validateEmail(formState.email)) {
      setFormError('emailError', 'Please enter a valid email address');
      return;
    }

    updateFormField('isProcessing', true);
    try {
      const response = await checkEmail({ email: formState.email });
      if (response?.success) {
        updateFormField('isOtpSent', true);
        setCountdownKey(prev => prev + 1);
        toastSuccess('OTP sent successfully');
      } else {
        toastError(response?.data?.errors?.[0]?.msg || 'Failed to send OTP');
      }
    } catch (error) {
      toastError(error?.response?.data?.errors?.[0]?.msg || 'Failed to send OTP');
    } finally {
      updateFormField('isProcessing', false);
    }
  }, [formState.email, resetFormErrors, validateEmail, setFormError, updateFormField]);

  const handleVerifyOtp = useCallback(async () => {
    resetFormErrors();
    
    if (!formState.otp) {
      setFormError('otpError', 'Please enter OTP');
      return;
    }
    
    if (!validateOtp(formState.otp)) {
      setFormError('otpError', 'Please enter a valid 6-digit OTP');
      return;
    }

    updateFormField('isProcessing', true);
    try {
      const response = await validateEmailOtp({ otp: formState.otp });
      if (response?.success) {
        updateFormField('otp', '');
        updateFormField('isOtpSent', false);
        await fetchUserDetails();
        toastSuccess('Email verified successfully');
      } else {
        toastError(response?.data?.errors?.[0]?.msg || 'Invalid OTP');
      }
    } catch (error) {
      toastError(error?.response?.data?.errors?.[0]?.msg || 'OTP verification failed');
    } finally {
      updateFormField('isProcessing', false);
    }
  }, [formState.otp, resetFormErrors, validateOtp, setFormError, updateFormField, fetchUserDetails]);

  // Payment functions
  const handlePaymentResponse = useCallback(async (response, paymentStatus, payload, messageError) => {
    try {
      const paymentStatusResponse = await easeBuzzPaymentStatus(response);
      if (paymentStatusResponse?.success) {
        window.location.href = paymentStatusResponse?.data;
      } else {
        const errorMsg = messageError || 
                        paymentStatusResponse?.response?.data?.data?.[0]?.msg || 
                        'Payment verification failed';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      toast.error('Payment verification failed');
    }
  }, []);

  const initializeEaseBuzzPayment = useCallback(async (item) => {
    const profileData = JSON.parse(localStorage.getItem("profile"));
    
    if (!profileData) {
      toast.error('Profile data not found. Please login again.');
      return;
    }

    updateFormField('isProcessing', true);
    
    try {
      const payload = {
        amount: item.amount,
        productId: item._id,
        email: profileData.email,
        phone: profileData.phone,
        productInfo: process.env.REACT_APP_COIN_PRODUCT,
        userId: profileData._id,
        firstname: profileData.gamerjiName,
        coins: item.coins,
        description: item.description,
        platform: "webportal",
      };

      const response = await easeBuzzHash(payload);
      // console.log("UUUUUUUUUUU", response.success);
      
      // Handle different response structures
      const responseData = response?.data || response;
      // console.log("UUUUUUUUUUU", response.success, responseData?.accessToken);
      if (responseData?.accessToken) {
        console.log("EaseBuzz payment hash generated successfully:", responseData);
        const options = {
          access_key: responseData.accessToken,
          onResponse: (paymentResponse) => {
            let paymentStatus = "";
            let messageError = "";
            
            switch (paymentResponse.status) {
              case "success":
                paymentStatus = "SUCCESS";
                break;
              case "failure":
                paymentStatus = "FAILED";
                messageError = "We couldn't process your payment.";
                break;
              case "userCancelled":
                paymentStatus = "USER_CANCELLED";
                messageError = "You have cancelled your payment process.";
                break;
              default:
                paymentStatus = paymentResponse.status.toUpperCase();
                messageError = "Payment failed. Please try again.";
                break;
            }
            
            handlePaymentResponse(paymentResponse, paymentStatus, payload, messageError);
          },
          theme: "#123456",
        };
        
        if (easebuzzCheckout) {
          easebuzzCheckout.initiatePayment(options);
        } else {
          toast.error('Payment gateway not initialized. Please refresh and try again.');
        }
      } else {
        // Handle API error response
        const errorMsg = responseData?.errors?.[0]?.msg || 
                        responseData?.message || 
                        'Failed to generate payment hash';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Error in EaseBuzz payment:", error);
      const errorMsg = error?.message || 
                      error?.originalError?.response?.data?.errors?.[0]?.msg || 
                      'Failed to initiate payment. Please try again.';
      toast.error(errorMsg);
    } finally {
      updateFormField('isProcessing', false);
    }
  }, [easebuzzCheckout, handlePaymentResponse, updateFormField]);

  const handlePurchase = useCallback(async () => {
    const profileLite = JSON.parse(localStorage.getItem("profileLite"));
    
    if (!profileLite) {
      toast.error('Profile data not found. Please login again.');
      return;
    }

    try {
      if (profileLite.address?.country === INDIA_COUNTRY_ID) {
        await initializeEaseBuzzPayment(reedemItem);
      } else {
        toast.error("Please select your country as India to purchase coins.");
      }
    } catch (error) {
      console.error('Purchase failed:', error);
      toast.error('Purchase failed. Please try again.');
    }
  }, [reedemItem, initializeEaseBuzzPayment]);

  // Event handlers
  const handleEmailChange = useCallback((e) => {
    const value = e.target.value;
    updateFormField('email', value);
    if (formState.emailError) {
      resetFormErrors();
    }
  }, [formState.emailError, updateFormField, resetFormErrors]);

  const handleOtpChange = useCallback((e) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) {
      setFormError('otpError', 'Please enter a valid 6-digit OTP');
      return;
    }
    updateFormField('otp', value);
    if (formState.otpError) {
      resetFormErrors();
    }
  }, [formState.otpError, updateFormField, resetFormErrors, setFormError]);

  // Countdown renderer
  const countdownRenderer = useCallback(({ minutes, seconds, completed }) => {
    if (completed) {
      setFormState(prev => ({ ...prev, otp: "", isOtpSent: false }));
      return <></>;
    }
    return <>Request for a new one in {minutes}:{seconds} seconds</>;
  }, []);

  // Effects
  useEffect(() => {
    if (isOpen) {
      fetchUserDetails();
    }
  }, [isOpen, fetchUserDetails]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setFormState({
        email: "",
        otp: "",
        isOtpSent: false,
        emailError: "",
        otpError: "",
        isLoading: false,
        isProcessing: false,
      });
    };
  }, []);

  // Render helpers
  const renderItemImage = () => {
    if (isBundle) {
      return (
        <div className="popup-title-img-wrap">
          <div>
            <img
              className="reward-store-popup-img"
              src={url?.IMAGE_URL + reedemItem?.avatar?.img?.default}
              alt="Bundle item"
            />
          </div>
          <h2 className="popup-header w-100">
            <img
              src={url?.IMAGE_URL + reedemItem?.virtualCurrency?.img?.default}
              className="icon-20"
              style={{ margin: "2px 8px 8px 2px" }}
              alt={reedemItem?.virtualCurrency?.name}
            />
            {reedemItem?.coins} {reedemItem?.virtualCurrency?.name}
            <br />+
          </h2>
          <h2 className="popup-header w-100">{title}</h2>
        </div>
      );
    }

    return (
      <div className="popup-title-img-wrap">
        <div>
          <img
            className="reward-store-popup-img"
            src={url?.IMAGE_URL + reedemItem?.image?.default}
            alt="Coin item"
          />
        </div>
        <h2 className="popup-header w-100">{reedemItem?.coins}</h2>
        <h2 className="popup-header w-100">{title}</h2>
      </div>
    );
  };

  const renderPurchaseButton = () => {
    const buttonText = (
      <>
        buy for
        {reedemItem?.currency?.code === "INR" ? (
          <>
            <span className="ms-1">{reedemItem?.currency?.symbol}</span>
            <span className="ms-1">{reedemItem?.amount}</span>
          </>
        ) : (
          <>
            <span className="ms-1">{reedemItem?.amount}</span>
            <span className="ms-1">
              <img
                className="verifyandredeem"
                src={url?.IMAGE_URL + reedemItem?.currency?.img?.default}
                alt={reedemItem?.name}
              />
            </span>
          </>
        )}
      </>
    );

    return (
      <button
        className="submit-button verify-text"
        onClick={handlePurchase}
        disabled={formState.isProcessing}
      >
        {formState.isProcessing ? 'Processing...' : buttonText}
      </button>
    );
  };

  if (formState.isLoading) {
    return <></>;
  }

  const isEmailVerified = userDetails?.email && userDetails?.isEmailVerified;

  return   (
    <div className="popupinner-wrapper">
      <div className="popup-overlay">
        <div className="popup-container">
          <button className="close-btn" onClick={onClose}>
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.8" filter="url(#filter0_b_2731_8186)">
                <circle
                  cx="16"
                  cy="16"
                  r="16"
                  fill="#82838E"
                  fillOpacity="0.5"
                />
              </g>
              <rect
                x="12.0531"
                y="10.9065"
                width="13.3333"
                height="1.96078"
                rx="0.980392"
                transform="rotate(45 12.0531 10.9065)"
                fill="#F4F4F4"
              />
              <rect
                x="10.6666"
                y="20.3345"
                width="13.3333"
                height="1.96078"
                rx="0.980392"
                transform="rotate(-45 10.6666 20.3345)"
                fill="#F4F4F4"
              />
              <defs>
                <filter
                  id="filter0_b_2731_8186"
                  x="-24"
                  y="-24"
                  width="80"
                  height="80"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feGaussianBlur in="BackgroundImageFix" stdDeviation="12" />
                  <feComposite
                    in2="SourceAlpha"
                    operator="in"
                    result="effect1_backgroundBlur_2731_8186"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_backgroundBlur_2731_8186"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </button>

          {renderItemImage()}

          <div className="space-between">
            {/* Email verification section */}
            {!isEmailVerified && (
              <div className="popup-body">
                <TextFieldWithButton
                  label="email ID"
                  placeholder="enter email id here"
                  value={formState.email}
                  onChange={handleEmailChange}
                  buttonText="Send OTP"
                  onButtonClick={handleSendOtp}
                  showButton={!formState.isOtpSent}
                  readOnly={userDetails?.isEmailVerified}
                  disabled={formState.isProcessing}
                />
                {formState.emailError && (
                  <div
                    className="error-message"
                    style={{ fontSize: "12px", position: "static" }}
                  >
                    {formState.emailError}
                  </div>
                )}
              </div>
            )}

            {/* OTP verification section */}
            {formState.isOtpSent && (
              <>
                <div className="popup-body">
                  <TextFieldWithButton
                    label="enter otp"
                    placeholder="enter otp"
                    value={formState.otp}
                    onChange={handleOtpChange}
                    showButton={false}
                    minLength={OTP_LENGTH}
                    maxLength={OTP_LENGTH}
                    disabled={formState.isProcessing}
                  />
                  {formState.otpError && (
                    <div
                      className="error-message"
                      style={{ fontSize: "12px", position: "static" }}
                    >
                      {formState.otpError}
                    </div>
                  )}
                </div>
                <div className="d-flex justify-content-center">
                  <Countdown
                    key={countdownKey}
                    date={new Date().setSeconds(new Date().getSeconds() + COUNTDOWN_DURATION)}
                    renderer={countdownRenderer}
                  />
                </div>
              </>
            )}

            {/* Action buttons */}
            {!isEmailVerified ? (
              <button
                className="submit-button verify-text"
                onClick={handleVerifyOtp}
                disabled={formState.isProcessing}
              >
                {formState.isProcessing ? 'Verifying...' : 'Verify'}
              </button>
            ) : (
              renderPurchaseButton()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyCoinPopup;

